import log from 'loglevel';

//eslint-disable-next-line no-restricted-imports
import type DxfArrayScanner from '../../DxfArrayScanner';
//eslint-disable-next-line no-restricted-imports
import { groupIs } from '../../ParseHelpers';
//eslint-disable-next-line no-restricted-imports
import { DXFSymbol } from '../../constants';
import {
  parseApplicationGroup,
  serializeApplicationDefinedGroups,
} from '../tables';
import type { ApplicationDefinedGroups } from '../tables';

export type IStyle = {
  applicationGroups?: ApplicationDefinedGroups;
  bigFontFileName?: string;
  fixedTextHeight?: number;
  fontFileName?: string;
  handle?: string;
  lastHeightUsed?: number;
  name: string;
  obliqueAngle?: number;
  ownerHandle?: string;
  standardFlags?: number;
  textGenerationFlags?: number;
  ttfPitchFamilyCharSetItalicAndBoldFlags?: number;
  widthFactor?: number;

  //Note: subMarkers insertion order must be maintained when serializing
  //subMarkers are necessary for eDrawings to import dxf we serialize
  //style use 1 or 2 subMarker types. 3rd and 4th are added for insurance
  subMarkerCount?: number;
  subMarker1?: string;
  subMarker2?: string;
  subMarker3?: string;
  subMarker4?: string;
};

const stylePropertyFromCode = new Map<number, keyof IStyle>([
  [2, 'name'],
  [3, 'fontFileName'],
  [4, 'bigFontFileName'],
  [5, 'handle'],
  [40, 'fixedTextHeight'],
  [41, 'widthFactor'],
  [42, 'lastHeightUsed'],
  [50, 'obliqueAngle'],
  [70, 'standardFlags'],
  [71, 'textGenerationFlags'],
  [330, 'ownerHandle'],
  [1071, 'ttfPitchFamilyCharSetItalicAndBoldFlags'],
]);

const codeFromStyleProperty = new Map<keyof IStyle, number>(
  Array.from(stylePropertyFromCode.entries()).map(([code, property]) => [
    property,
    code,
  ]),
);

//delete special cases that are are handled differently when serializing and parsing
stylePropertyFromCode.delete(2);

codeFromStyleProperty.set('subMarker1', 100);
codeFromStyleProperty.set('subMarker2', 100);
codeFromStyleProperty.set('subMarker3', 100);
codeFromStyleProperty.set('subMarker4', 100);

export const parseStyles = (
  scanner: DxfArrayScanner,
): Record<string, IStyle> => {
  const styles = {} as Record<string, IStyle>;
  let style = {} as IStyle;
  let styleName: string | undefined;

  log.debug('Style {');
  let curr = scanner.next();
  while (!groupIs(curr, 0, DXFSymbol.EndTable)) {
    const property = stylePropertyFromCode.get(curr.code);
    if (property != null) {
      (style[property] as string | number) = curr.value as string | number;
      curr = scanner.next();
    } else {
      switch (curr.code) {
        case 2:
          style.name = curr.value as string;
          styleName = curr.value as string;
          curr = scanner.next();
          break;
        case 100:
          if (!style.subMarkerCount) {
            style.subMarkerCount = 1;
          } else {
            style.subMarkerCount++;
          }
          style[`subMarker${style.subMarkerCount}` as 'subMarker1'] =
            curr.value as string;
          curr = scanner.next();
          break;
        case 102:
          if (
            typeof curr.value === 'string' &&
            curr.value.startsWith('{') &&
            curr.value.length > 0
          ) {
            const name = curr.value.substring(1);
            if (!style.applicationGroups) style.applicationGroups = {};
            style.applicationGroups[name] = parseApplicationGroup(scanner);
          }
          curr = scanner.next();
          break;
        case 0:
          log.debug('}');
          if (typeof styleName === 'string') {
            styles[styleName] = style; //save last style
          }
          style = {} as IStyle;
          log.debug('Style {');
          curr = scanner.next();
          break;
        default:
          curr = scanner.next();
      }
    }
  }

  log.debug('}');
  //styleName should be defined in while loop above
  if (typeof styleName === 'string') {
    styles[styleName] = style; //save last style
  }
  return styles;
};

export const serializeStyles = function* (
  styles: Record<string, IStyle>,
): IterableIterator<string> {
  for (const style of Object.values(styles) as IStyle[]) {
    yield '0';
    yield DXFSymbol.Style;
    for (const [property, value] of Object.entries(style) as [
      keyof IStyle,
      IStyle[keyof IStyle],
    ][]) {
      const code = codeFromStyleProperty.get(property);
      if (code != null) {
        yield `${code}`;
        yield `${value}`;
      } else {
        switch (property) {
          case 'applicationGroups':
            yield* serializeApplicationDefinedGroups(
              value as ApplicationDefinedGroups,
            );
            break;
        }
      }
    }
  }
};
